// app/javascript/components/shared/AppShell.tsx
import React, { useState, useEffect } from 'react'
import { usePage, Link } from '@inertiajs/react'
import { ThemeProvider } from './ThemeProvider'
import { ThemeMenu } from './ThemeMenu'
import { TermsAcceptanceModal } from './TermsAcceptanceModal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import type { PageProps } from '@/types'
import {
  LayoutDashboard, Layers, ClipboardList, Sparkles, GraduationCap,
  BookOpen, Target, Network, Users, Palette, Activity, ExternalLink,
  ShieldCheck, LogOut, Menu, ChevronRight, PanelLeftClose, PanelLeftOpen,
  Compass, X, Newspaper, Globe
} from 'lucide-react'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  exact?: boolean
  external?: boolean
  badge?: string | number
}

interface NavSection {
  title?: string
  items: NavItem[]
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const pageProps = usePage<PageProps>().props
  const { auth, flash } = pageProps
  const { toast } = useToast()
  const user = auth?.user

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('prepkiya_admin_sidebar_collapsed') === 'true'
    }
    return false
  })

  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem('prepkiya_admin_sidebar_collapsed', String(collapsed))
  }, [collapsed])

  useEffect(() => {
    if (flash?.notice) {
      toast({ title: 'Success', description: flash.notice })
    }
    if (flash?.alert) {
      toast({ title: 'Alert', description: flash.alert, variant: 'destructive' })
    }
  }, [flash])

  // Ensure window/body does not scroll when inside admin shell
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlHeight = document.documentElement.style.height
    const originalBodyHeight = document.body.style.height

    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.height = '100%'
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100%'

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow
      document.documentElement.style.height = originalHtmlHeight
      document.body.style.overflow = originalBodyOverflow
      document.body.style.height = originalBodyHeight
    }
  }, [])

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  const navSections: NavSection[] = [
    {
      title: 'CORE',
      items: [
        { label: 'Overview', href: '/admin', icon: LayoutDashboard, exact: true },
        { label: 'Question Bank', href: '/admin/questions', icon: Layers },
        { label: 'Tests', href: '/admin/tests', icon: ClipboardList },
        { label: 'Test Generator', href: '/admin/test_generator', icon: Sparkles },
      ]
    },
    {
      title: 'CURRICULUM & TAXONOMY',
      items: [
        { label: 'Standards', href: '/admin/standards', icon: GraduationCap },
        { label: 'Subjects', href: '/admin/subjects', icon: BookOpen },
        { label: 'Exams', href: '/admin/exams', icon: Target },
        { label: 'Syllabi', href: '/admin/syllabi', icon: Network },
      ]
    },
    {
      title: 'MANAGEMENT & SYSTEM',
      items: [
        { label: 'Blog Posts', href: '/admin/blog_posts', icon: Newspaper },
        { label: 'Users', href: '/admin/users', icon: Users },
        { label: 'Terms of Service', href: '/admin/terms_versions', icon: ShieldCheck },
        { label: 'Themes', href: '/admin/themes', icon: Palette },
        { label: 'Sidekiq Web', href: '/sidekiq', icon: Activity, external: true },
      ]
    }
  ]

  const isItemActive = (item: NavItem) => {
    if (item.external) return false
    if (item.exact) {
      return currentPath === item.href
    }
    return currentPath === item.href || currentPath.startsWith(item.href + '/') || currentPath.startsWith(item.href + '?')
  }

  // Mobile Bottom Navigation: 5 Primary Tabs
  const mobilePrimaryTabs = [
    { label: 'Overview', href: '/admin', icon: LayoutDashboard, exact: true },
    { label: 'Questions', href: '/admin/questions', icon: Layers },
    { label: 'Exams', href: '/admin/exams', icon: Target },
    { label: 'Generator', href: '/admin/test_generator', icon: Sparkles },
    { label: 'Users', href: '/admin/users', icon: Users },
  ]

  const isPrimaryTabActive = (tab: { href: string, exact?: boolean }) => {
    if (tab.exact) return currentPath === tab.href
    return currentPath === tab.href || currentPath.startsWith(tab.href + '/') || currentPath.startsWith(tab.href + '?')
  }

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'AD'
  const roleLabel = user?.role === 'super_admin' ? 'Super Admin' : (user?.role === 'reviewer' ? 'Reviewer' : 'Admin')

  const DesktopSidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <div className="flex flex-col h-full bg-card/95 backdrop-blur-glass border-r border-border shadow-glass-md transition-all duration-300 select-none">
      {/* Header with Logo & Console Badge */}
      <div className={`py-4 border-b border-border/40 flex items-center transition-all ${isCollapsed ? 'px-2 flex-col gap-3 justify-center' : 'px-4 justify-between'}`}>
        <Link href="/" title="PrepKiya Home" className="flex items-center gap-3 group min-w-0">
          <img src="/logo.svg" alt="PrepKiya Logo" className="w-9 h-9 rounded-xl object-cover shrink-0 transition-transform group-hover:scale-105 border border-border" />
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="text-lg font-extrabold text-foreground tracking-tight truncate">
                PrepKiya
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-md border border-primary/20">
                  Admin Console
                </span>
              </div>
            </div>
          )}
        </Link>

        {/* Desktop Collapse Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className={`hidden lg:flex text-muted-foreground hover:text-foreground hover:bg-muted/60 shrink-0 ${
            isCollapsed ? 'h-7 w-7' : 'h-8 w-8'
          }`}
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {/* Grouped Navigation */}
      <nav className="flex-1 px-2.5 py-3 space-y-4 overflow-y-auto">
        {navSections.map((section, idx) => (
          <div key={section.title || idx} className="space-y-1">
            {section.title && !isCollapsed && (
              <p className="text-[10px] font-extrabold text-muted-foreground/70 uppercase tracking-widest px-3 pt-1 pb-1">
                {section.title}
              </p>
            )}
            {isCollapsed && idx > 0 && (
              <div className="my-2 border-t border-border/40 mx-2" />
            )}

            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon
                const active = isItemActive(item)

                if (item.external) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      title={isCollapsed ? item.label : undefined}
                      className={`flex items-center rounded-xl text-xs font-semibold transition-all group ${
                        isCollapsed ? 'justify-center p-2.5' : 'px-3.5 py-2.5 gap-3'
                      } text-muted-foreground hover:bg-muted/60 hover:text-foreground border border-transparent`}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                        </>
                      )}
                    </a>
                  )
                }

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    title={isCollapsed ? item.label : undefined}
                    className={`relative flex items-center rounded-xl text-xs font-semibold transition-all group ${
                      isCollapsed ? 'justify-center p-2.5' : 'px-3.5 py-2.5 gap-3'
                    } ${
                      active
                        ? 'bg-primary/15 text-primary border border-primary/30 shadow-glass-sm'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground border border-transparent'
                    }`}
                  >
                    <Icon className={`h-4 w-4 transition-colors shrink-0 ${active ? 'text-primary' : 'group-hover:text-foreground'}`} />
                    {!isCollapsed && <span className="flex-1 truncate">{item.label}</span>}
                    {active && !isCollapsed && <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0" />}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Sidebar Footer — Admin Profile & Sign Out */}
      {user && (
        <div className={`border-t border-border/40 transition-all ${isCollapsed ? 'p-2 flex flex-col items-center gap-2' : 'p-2.5'}`}>
          <div className={`flex items-center gap-2 ${isCollapsed ? 'flex-col' : 'justify-between'}`}>
            <Link
              href="/admin/profile"
              title={isCollapsed ? `${user.name} (${roleLabel})` : undefined}
              className={`flex items-center gap-2.5 min-w-0 rounded-xl transition-all group flex-1 ${
                isCollapsed
                  ? 'p-1.5 justify-center hover:bg-muted/60'
                  : 'p-2 hover:bg-muted/50 border border-transparent hover:border-border/50'
              } ${
                currentPath.startsWith('/admin/profile')
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : ''
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-primary text-primary-fg flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 overflow-hidden" title={user.name}>
                {user.avatar_base64 || user.avatar_url ? (
                  <img
                    src={user.avatar_base64 || user.avatar_url || ''}
                    alt={user.name}
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              {!isCollapsed && (
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">{user.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge variant="outline" className="text-[9px] font-semibold px-1 py-0 border-primary/30 text-primary bg-primary/5">
                      {roleLabel}
                    </Badge>
                  </div>
                </div>
              )}
            </Link>

            <Link
              href="/users/sign_out"
              method="delete"
              as="button"
              title="Sign Out"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all shrink-0"
            >
              <LogOut className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <ThemeProvider initialThemeName={user?.theme_name} portal="admin">
      <div className="app-shell-root fixed inset-0 flex h-full w-full overflow-hidden bg-background text-foreground select-none">
        {/* Desktop Collapsible Sidebar (Hidden on Mobile/Tablet) */}
        <aside className={`hidden lg:flex lg:flex-col shrink-0 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
          <DesktopSidebarContent isCollapsed={collapsed} />
        </aside>

        {/* Mobile Slide-out Drawer */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="absolute left-0 top-0 h-full w-[84vw] max-w-[340px] z-50 bg-card rounded-r-3xl p-4 sm:p-5 flex flex-col justify-between shadow-glass-lg border-r border-border/80 transition-transform duration-300 ease-out animate-in slide-in-from-left">
              {/* Drawer Header: Logo + Brand + Close Button */}
              <div className="flex items-center justify-between pb-3.5 border-b border-border/40 shrink-0">
                <Link
                  href="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 min-w-0"
                >
                  <img src="/logo.svg" alt="PrepKiya Logo" className="w-9 h-9 rounded-xl object-cover shrink-0 border border-border" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-base font-extrabold text-foreground tracking-tight truncate">
                      PrepKiya
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20">
                        Admin Console
                      </span>
                    </div>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="w-9 h-9 rounded-full border border-border/70 bg-card hover:bg-muted/40 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0 shadow-2xs"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Drawer Grouped Navigation List */}
              <nav className="flex-1 py-2 overflow-y-auto min-h-0 space-y-3">
                {navSections.map((section, idx) => (
                  <div key={section.title || idx} className="space-y-1">
                    {section.title && (
                      <p className="text-[10px] font-extrabold text-muted-foreground/70 uppercase tracking-widest px-3 pt-2 pb-0.5">
                        {section.title}
                      </p>
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = item.icon
                        const active = isItemActive(item)

                        if (item.external) {
                          return (
                            <a
                              key={item.href}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => setSidebarOpen(false)}
                              className="flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-xl text-foreground/80 hover:bg-muted/40 hover:text-foreground transition-all"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-lg bg-muted/60 text-muted-foreground flex items-center justify-center shrink-0">
                                  <Icon className="h-4 w-4" />
                                </div>
                                <span className="truncate">{item.label}</span>
                              </div>
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                            </a>
                          )
                        }

                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-xl transition-all ${
                              active
                                ? 'text-primary bg-primary/10 font-bold border border-primary/25'
                                : 'text-foreground/80 hover:bg-muted/40 hover:text-foreground'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                active ? 'bg-primary/20 text-primary' : 'bg-muted/60 text-muted-foreground'
                              }`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <span className="truncate">{item.label}</span>
                            </div>
                            <ChevronRight className={`h-4 w-4 ${active ? 'text-primary' : 'text-muted-foreground/50'}`} />
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </nav>

              {/* Drawer Footer — Profile Card & Sign Out */}
              {user && (
                <div className="pt-3 border-t border-border/40 shrink-0">
                  <div className="p-2.5 rounded-2xl border border-border/70 bg-muted/20 flex items-center justify-between gap-2 shadow-2xs">
                    <Link
                      href="/admin/profile"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-2.5 min-w-0 flex-1 group"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary text-primary-fg flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 overflow-hidden">
                        {user.avatar_base64 || user.avatar_url ? (
                          <img
                            src={user.avatar_base64 || user.avatar_url || ''}
                            alt={user.name}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">{user.name}</p>
                        <Badge variant="outline" className="text-[9px] font-semibold px-1 py-0 border-primary/30 text-primary bg-primary/5 mt-0.5">
                          {roleLabel}
                        </Badge>
                      </div>
                    </Link>

                    <Link
                      href="/users/sign_out"
                      method="delete"
                      as="button"
                      title="Sign Out"
                      className="w-8 h-8 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0"
                    >
                      <LogOut className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}

        {/* Main Content Area: Flex Column */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 h-full overflow-hidden">
          {/* Desktop Topbar */}
          <header className="hidden lg:flex sticky top-0 z-30 items-center justify-between px-6 h-14 border-b border-border/60 bg-card/95 backdrop-blur-glass shrink-0 gap-3 shadow-2xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-bold text-muted-foreground">Admin Console</span>
              <span className="text-muted-foreground/40">/</span>
              <span className="text-xs font-bold text-foreground truncate">
                {currentPath === '/admin' ? 'Dashboard Overview' : currentPath.split('/')[2]?.replace(/_/g, ' ').toUpperCase() || 'Admin'}
              </span>
            </div>

            {/* Desktop Topbar Right Controls */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg text-xs font-semibold border-border bg-background hover:bg-muted/50 gap-1.5"
                asChild
              >
                <Link href="/">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Public Site</span>
                </Link>
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg text-xs font-semibold border-border bg-background hover:bg-muted/50 gap-1.5"
                asChild
              >
                <Link href="/student">
                  <Compass className="h-3.5 w-3.5 text-secondary" />
                  <span>Student Portal</span>
                </Link>
              </Button>

              <ThemeMenu />

              {user ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 px-2.5"
                  asChild
                >
                  <Link href="/users/sign_out" method="delete" as="button">
                    <LogOut className="h-3.5 w-3.5 mr-1" />
                    <span>Sign out</span>
                  </Link>
                </Button>
              ) : (
                <Button size="sm" className="h-8 text-xs font-bold" asChild>
                  <Link href="/users/sign_in">Sign in</Link>
                </Button>
              )}
            </div>
          </header>

          {/* Mobile Topbar */}
          <header className="lg:hidden shrink-0 z-20 flex items-center justify-between px-3 h-12 border-b border-border/60 bg-card/95 backdrop-blur-glass shadow-2xs">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-foreground hover:bg-muted/60 shrink-0"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Link href="/" className="flex items-center" title="PrepKiya Home">
                <img src="/logo.svg" alt="PrepKiya" className="w-7 h-7 rounded-lg object-cover shrink-0 border border-border" />
              </Link>
            </div>

            <div className="flex items-center gap-1.5">
              <ThemeMenu />
            </div>
          </header>

          {/* Page View Container with generous bottom clearance for mobile 5-tab bar */}
          <main className="flex-1 min-h-0 overflow-y-auto min-w-0 pb-20 lg:pb-6">
            <div className="container max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3.5 sm:py-6 min-w-0 overflow-x-hidden">
              {children}
            </div>
          </main>
        </div>

        {/* Mobile Sticky 5-Tab Bottom Navigation Bar (< lg) */}
        <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden border-t border-border bg-card/95 backdrop-blur-md grid grid-cols-5 py-1 pb-[env(safe-area-inset-bottom,0px)] shadow-glass-md select-none">
          {mobilePrimaryTabs.map((tab) => {
            const Icon = tab.icon
            const active = isPrimaryTabActive(tab)
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 transition-all ${
                  active
                    ? 'text-primary font-bold'
                    : 'text-muted-foreground hover:text-foreground font-medium'
                }`}
              >
                <div className={`p-1.5 rounded-xl transition-all ${active ? 'bg-primary/10 text-primary' : ''}`}>
                  <Icon className={`h-5 w-5 ${active ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
                </div>
                <span className="text-[10px] tracking-tight leading-none mt-0.5">{tab.label}</span>
                {active && (
                  <span className="absolute -bottom-px left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            )
          })}
        </div>

        <TermsAcceptanceModal />
        <Toaster />
      </div>
    </ThemeProvider>
  )
}
